import React from 'react';
import Icon from '../../../components/AppIcon';

const PerformanceCard = ({ company, onExpand, isExpanded }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const getPerformanceColor = (value) => {
    if (value >= 10) return 'text-success';
    if (value >= 0) return 'text-warning';
    return 'text-error';
  };

  const getCollectionEfficiencyColor = (rate) => {
    if (rate >= 90) return 'text-success';
    if (rate >= 70) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200">
      {/* Company Header with Yellow Background */}
      <div className="bg-accent px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-accent-foreground">
            {company.name}
          </h3>
          <button
            onClick={() => onExpand(company.id)}
            className="p-1 hover:bg-accent-foreground/10 rounded transition-colors duration-150"
          >
            <Icon 
              name={isExpanded ? "ChevronUp" : "ChevronDown"} 
              size={16} 
              className="text-accent-foreground"
            />
          </button>
        </div>
      </div>

      {/* Main Metrics */}
      <div className="p-4">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Total Sales</div>
            <div className="text-lg font-semibold text-foreground">
              {formatCurrency(company.totalSales)}
            </div>
            <div className={`text-xs ${getPerformanceColor(company.salesGrowth)}`}>
              {formatPercentage(company.salesGrowth)}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Collections</div>
            <div className="text-lg font-semibold text-foreground">
              {formatCurrency(company.totalCollections)}
            </div>
            <div className={`text-xs ${getCollectionEfficiencyColor(company.collectionRate)}`}>
              {company.collectionRate.toFixed(1)}% rate
            </div>
          </div>
        </div>

        {/* Performance Indicators */}
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">Target Achievement</span>
              <span className="font-medium text-foreground">
                {company.targetAchievement.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  company.targetAchievement >= 100 
                    ? 'bg-success' 
                    : company.targetAchievement >= 80 
                    ? 'bg-warning' :'bg-error'
                }`}
                style={{ width: `${Math.min(company.targetAchievement, 100)}%` }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">Collection Efficiency</span>
              <span className="font-medium text-foreground">
                {company.collectionRate.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  getCollectionEfficiencyColor(company.collectionRate).includes('success') 
                    ? 'bg-success' : getCollectionEfficiencyColor(company.collectionRate).includes('warning')
                    ? 'bg-warning' :'bg-error'
                }`}
                style={{ width: `${company.collectionRate}%` }}
              />
            </div>
          </div>
        </div>

        {/* Trend Sparkline Placeholder */}
        <div className="mt-4 pt-3 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">7-day trend</span>
            <div className="flex items-center space-x-1">
              {company.weeklyTrend.map((value, index) => (
                <div
                  key={index}
                  className={`w-1 rounded-full ${
                    value > 0 ? 'bg-success' : value < 0 ? 'bg-error' : 'bg-muted-foreground'
                  }`}
                  style={{ height: `${Math.abs(value) * 2 + 8}px` }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-border space-y-3">
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <span className="text-muted-foreground">Outstanding:</span>
                <span className="ml-2 font-medium text-foreground">
                  {formatCurrency(company.outstanding)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Avg. Deal Size:</span>
                <span className="ml-2 font-medium text-foreground">
                  {formatCurrency(company.avgDealSize)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Active Deals:</span>
                <span className="ml-2 font-medium text-foreground">
                  {company.activeDeals}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Last Updated:</span>
                <span className="ml-2 font-medium text-foreground">
                  {company.lastUpdated}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceCard;